import React, { useEffect, useRef, useState } from "react";
import { useJourney } from "./JourneyProvider";
import { useAllowMeFingerprint } from "../../hooks/useAllowMeDeviceId";
import { validateDevice } from "../../services/allowService";

const CpfScreen: React.FC = () => {
  const { styles, next, cpf, journey, ip, fingerprint: contextFingerprint } = useJourney();
  const cpfPrefix = cpf?.replace(/\D/g, "").slice(0, 5) ?? "";

  const [digits, setDigits] = useState<string[]>(Array(5).fill(""));
  const [error, setError] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  // Usa o fingerprint do contexto se já existir (carregado pelo provider), senão carrega (fallback)
  // Mas como o provider já carrega, podemos usar direto ou manter o hook local como segurança
  // Se usarmos useAllowMeFingerprint aqui novamente, ele apenas retornará o valor se o contexto for o mesmo
  // ou reinicializará. O ideal é usar o do contexto.
  const fingerprint = contextFingerprint; 
  const [isLoading, setIsLoading] = useState(false);

  const clientName =
    journey?.nomeCliente ||
    journey?.client?.corporateName ||
    (journey as any)?.client?.name ||
    "";

  useEffect(() => {
    const filled = digits.every((d) => d !== "");
    if (!filled) {
      setError(false);
      return;
    }
    const typed = digits.join("");
    setError(typed !== cpfPrefix);
  }, [digits, cpfPrefix]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 4) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const isValid = digits.join("") === cpfPrefix;

  const handleValidateDevice = async () => {
    // Se a tela de localização estiver habilitada, apenas avançamos.
    // O envio (validateDevice) será feito lá, após a permissão de GPS.
    // O fingerprint JÁ foi inicializado pelo hook (para autenticar sessão), mas será recoletado lá.
    const isLocationEnabled = journey?.screens?.some((s: any) => {
      const val = s.enabled ?? s.Enabled;
      return (
        (val === true || val === "true" || val === 1) &&
        s.id?.toLowerCase() === "location"
      );
    });

    if (isLocationEnabled) {
      next();
      return;
    }

    if (!fingerprint) {
      console.warn("Fingerprint ainda não carregado.");
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        integrationName: "login",
        userId: cpf,
        userIp: ip,
        event: fingerprint,
        metadata: {
          email: journey?.email || undefined,
          cpf: cpf?.replace(/\D/g, ""),
          phones: [journey?.tel ?? ""].filter(Boolean),
        },
        protocol: journey?.protocol,
      };
      const { data } = await validateDevice(payload);
      console.log("Validação de device concluída:", data);
      next();
    } catch (err) {
      console.error("❌ Erro ao validar device:", err);
      alert("Falha ao validar dispositivo. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 15,
        height: "100%",
        justifyContent: "center",
      }}
    >
      <p style={{ color: styles.textColor, fontSize: 14 }}>
        Olá <strong>{clientName || ""}</strong>!{" "}
        {styles.initialText ||
          "Seja bem-vindo à Serasa Experian. Leia a Política de Privacidade antes de prosseguir"}
      </p>

      <p style={{ color: styles.textColor, fontWeight: "bold", fontSize: 15 }}>
        Informe os 5 primeiros dígitos do seu CPF:
      </p>

      <div style={{ display: "flex", gap: 8 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            autoComplete="one-time-code"
            value={digits[i]}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            style={{
              width: 40,
              height: 50,
              textAlign: "center",
              fontSize: 24,
              border: `1px solid ${styles.textColor}`,
              borderRadius: 6,
              outlineColor: isValid
                ? "green"
                : error
                ? "red"
                : styles.buttonColor,
            }}
          />
        ))}
      </div>

      {error && (
        <p style={{ color: "red", fontSize: 13 }}>
          Os números informados não coincidem com o CPF.
        </p>
      )}

      <button
        onClick={handleValidateDevice}
        disabled={!isValid || !fingerprint || isLoading}
        aria-busy={isLoading}
        style={{
          width: "100%",
          padding: "12px 0",
          backgroundColor: styles.buttonColor,
          opacity: !isValid || !fingerprint || isLoading ? 0.5 : 1,
          border: "none",
          borderRadius: 6,
          color: styles.buttonLabelColor,
          fontWeight: 600,
          fontSize: 16,
          cursor:
            !isValid || !fingerprint || isLoading ? "default" : "pointer",
          transition: "opacity 0.2s ease",
        }}
      >
        {isLoading
          ? "Validando..."
          : isValid && !fingerprint
          ? "Carregando..."
          : "Verificar"}
      </button>
    </div>
  );
};

export default CpfScreen;
